import React, { useEffect } from 'react';
import { Auth0Provider, AppState, useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';

import { authEnabled, auth0Config, missingAuthEnvVars } from './config';
import AuthConfigErrorPage from './AuthConfigErrorPage';
import { registerLogout } from '../api/logoutRegistry';

/**
 * Registers a module-level logout handler so the axios interceptor can
 * trigger a full Auth0 logout on 401 (architect directive). Runs once
 * per Auth0Provider mount.
 */
const LogoutRegistrar: React.FC = () => {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();
  useEffect(() => {
    registerLogout(() => {
      try {
        window.localStorage.removeItem('activeTenantCode');
      } catch {
        /* ignore */
      }
      queryClient.clear();
      logout({ logoutParams: { returnTo: window.location.origin } });
    });
  }, [logout, queryClient]);
  return null;
};

/**
 * Inner wrapper — uses react-router's `useNavigate` to restore deep links
 * after Auth0's redirect callback. Must be rendered inside BrowserRouter.
 */
const Auth0InnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const onRedirectCallback = (appState?: AppState) => {
    const target = appState?.returnTo;
    if (target) navigate(target, { replace: true });
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: auth0Config.audience,
      }}
      // "memory" keeps tokens out of localStorage, closing the XSS
      // exfiltration window. Refresh tokens still work via rotation; Auth0
      // tenant must have rotation + reuse detection enabled.
      cacheLocation="memory"
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
    >
      <LogoutRegistrar />
      {children}
    </Auth0Provider>
  );
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!authEnabled) {
    // Local dev bypass: no CLIENT_ID configured → auth is disabled entirely.
    return <>{children}</>;
  }

  const missing = missingAuthEnvVars();
  if (missing.length > 0) {
    // CLIENT_ID is set but other required vars are missing — fail loud
    // rather than silently falling back to defaults.
    return <AuthConfigErrorPage missing={missing} />;
  }

  return <Auth0InnerProvider>{children}</Auth0InnerProvider>;
};

export default AuthProvider;
